import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { ShieldCheck, ShieldAlert, RefreshCw, ArrowRight, Loader2 } from 'lucide-react';
import logger from '@/lib/logger';
import { CoverageSparkline } from '@/components/dashboard/CoverageSparkline';

function formatRelativeTime(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function StatPill({ label, ok, errors, warnings, skipped }) {
  let pillClass = 'bg-emerald-50 border-emerald-200 text-emerald-700';
  let label2 = 'Clean';
  if (skipped) {
    pillClass = 'bg-slate-50 border-slate-200 text-slate-500';
    label2 = 'Skipped';
  } else if (!ok) {
    pillClass = 'bg-red-50 border-red-200 text-red-700';
    label2 = `${errors} error${errors === 1 ? '' : 's'}`;
  } else if (warnings > 0) {
    pillClass = 'bg-amber-50 border-amber-200 text-amber-700';
    label2 = `${warnings} warning${warnings === 1 ? '' : 's'}`;
  }
  return (
    <div className={`flex items-center justify-between border rounded-sm px-2.5 py-1.5 ${pillClass}`} data-testid={`build-health-${label.toLowerCase()}`}>
      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-80">{label}</span>
      <span className="text-xs font-semibold">{label2}</span>
    </div>
  );
}

function CoveragePill({ coverage }) {
  let pillClass = 'bg-red-50 border-red-200 text-red-700';
  if (coverage >= 70) pillClass = 'bg-emerald-50 border-emerald-200 text-emerald-700';
  else if (coverage >= 50) pillClass = 'bg-amber-50 border-amber-200 text-amber-700';
  return (
    <div className={`flex items-center justify-between border rounded-sm px-2.5 py-1.5 ${pillClass}`} data-testid="build-health-typehints">
      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-80">Type Hints</span>
      <span className="text-xs font-semibold">{coverage}%</span>
    </div>
  );
}

export function BuildHealthBadge() {
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(false);

  const fetchData = useCallback(() => {
    Promise.all([api.getCodeQualitySummary(), api.getCodeQualityTrend(7)])
      .then(([summaryRes, trendRes]) => {
        setSummary(summaryRes.data?.summary || null);
        setTrend(trendRes.data?.points || []);
        setError(false);
      })
      .catch(err => { logger.error('Build health fetch failed:', err); setError(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRescan = async () => {
    setScanning(true);
    try {
      await api.runCodeQualityScan();
      fetchData();
    } catch (err) {
      logger.error('Re-scan failed:', err);
    }
    setScanning(false);
  };

  if (loading || error || !summary) return null;

  const { overall_passed, backend, frontend, type_hints, timestamp } = summary;
  const HeaderIcon = overall_passed ? ShieldCheck : ShieldAlert;
  const headerIconClass = overall_passed ? 'text-emerald-600' : 'text-red-600';
  const headerLabel = overall_passed ? 'All checks passing' : 'Action needed';

  return (
    <div className="border border-slate-200 rounded-sm bg-white p-3 sm:p-4 mb-6" data-testid="build-health-badge">
      <div className="flex items-start sm:items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <HeaderIcon className={`w-4 h-4 shrink-0 ${headerIconClass}`} />
          <h2 className="font-heading font-semibold text-slate-900 text-sm">Build Health</h2>
          <span className="text-[10px] text-slate-400 hidden sm:inline">·&nbsp;&nbsp;Last scan {formatRelativeTime(timestamp)}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={handleRescan}
            disabled={scanning}
            className="text-xs text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 px-2 py-1 rounded-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="build-health-rescan"
            title="Run a fresh code quality scan"
          >
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Re-scan</span>
          </button>
          <Link
            to="/admin/code-quality"
            className="text-xs text-[#0047AB] hover:underline flex items-center gap-1 px-1"
            data-testid="build-health-details"
          >
            Details <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        {headerLabel} <span className="text-slate-400 sm:hidden">· {formatRelativeTime(timestamp)}</span>
      </p>
      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
        <StatPill label="Ruff" ok={backend.passed} errors={backend.errors} warnings={backend.warnings} skipped={backend.skipped} />
        <StatPill label="ESLint" ok={frontend.passed} errors={frontend.errors} warnings={frontend.warnings} skipped={frontend.skipped} />
        <CoveragePill coverage={type_hints?.coverage_pct ?? 0} />
      </div>
      {trend.length >= 2 && (
        <div className="mt-3 pt-3 border-t border-slate-100 overflow-x-auto">
          <CoverageSparkline points={trend} />
        </div>
      )}
    </div>
  );
}
